import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

export interface LanguageSwitchOutcome {
  activated?: () => void;
  failed?: () => void;
}

/**
 * Changes the language of the running app (SPEED-888).
 *
 * `TranslocoService.setActiveLang()` does not wait for the dictionary, and Transloco does not emit
 * `langChanges$` again when the dictionary arrives. A `computed()` that reads `injectActiveLang()` and
 * renders during the load would therefore cache raw keys until the next language change.
 *
 * Activating only after the load covers every `langChanges$` consumer: the old language stays on screen
 * until the dictionary is in memory, then everything changes together.
 *
 * Whatever else records the language (the selector, `speed.lang`, `<html lang>`, other tabs) belongs in
 * `activated`: a failed load leaves the app on the previous language, and those must keep saying so.
 * A choice replaced by a later one reports neither outcome.
 */
@Injectable({ providedIn: 'root' })
export class LanguageSwitchService {
  private readonly transloco = inject(TranslocoService);
  private requested: string | null = null;

  switchTo(lang: string, outcome: LanguageSwitchOutcome = {}): void {
    this.requested = lang;
    this.transloco.load(lang).subscribe({
      next: () => {
        // A slow earlier response must not override a later choice.
        if (this.requested === lang) {
          this.transloco.setActiveLang(lang);
          outcome.activated?.();
        }
      },
      // Load failed: stay on the current language rather than show raw keys everywhere.
      error: () => {
        if (this.requested === lang) {
          outcome.failed?.();
        }
      },
    });
  }
}
